import { Texture } from 'pixi.js';

/**
 * Аватарки (ТЗ §4.7).
 *
 * Текстура готовится СРАЗУ круглой: маска на 200 спрайтов убивает батчинг,
 * а обрезка при подготовке стоит один раз на зрителя.
 *
 * Фолбэк — не серый круг, а детерминированный identicon из хэша userId.
 * Шар без картинки ломает главную ценность формата: зритель должен узнавать
 * себя на экране.
 */

const SIZE = 96;

/** Сколько ждать перед уничтожением вытесненной текстуры. */
const GRACE_MS = 30_000;

function hash32(str: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

function circleCanvas(): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = SIZE;
  c.height = SIZE;
  const ctx = c.getContext('2d')!;
  ctx.beginPath();
  ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2, 0, Math.PI * 2);
  ctx.clip();
  return [c, ctx];
}

/** Геометрический identicon: 5×5, зеркальный по вертикальной оси. */
export function drawIdenticon(userId: string): HTMLCanvasElement {
  const h = hash32(userId);
  const hue = h % 360;
  const [c, ctx] = circleCanvas();

  ctx.fillStyle = `hsl(${hue} 45% 22%)`;
  ctx.fillRect(0, 0, SIZE, SIZE);

  ctx.fillStyle = `hsl(${(hue + 40) % 360} 78% 62%)`;
  const cell = SIZE / 5;
  let bits = h;
  for (let x = 0; x < 3; x++) {
    for (let y = 0; y < 5; y++) {
      bits = Math.imul(bits ^ (bits >>> 13), 0x5bd1e995);
      if ((bits >>> 7) % 100 < 48) continue;
      ctx.fillRect(x * cell, y * cell, cell + 0.5, cell + 0.5);
      if (x < 2) ctx.fillRect((4 - x) * cell, y * cell, cell + 0.5, cell + 0.5);
    }
  }
  return c;
}

/**
 * Круглая текстура из произвольного URL — портрет команды в троне.
 * Возвращается сразу, картинка подставляется по мере загрузки.
 */
export function loadCircleTexture(url: string, size = 256): Texture {
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d')!;
  const tex = Texture.from(c);

  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    ctx.clearRect(0, 0, size, size);
    ctx.save();
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    ctx.clip();
    // Вписываем по короткой стороне, чтобы портрет не сплющивался.
    const k = Math.max(size / img.width, size / img.height);
    const w = img.width * k;
    const h = img.height * k;
    ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
    ctx.restore();
    tex.source.update();
  };
  img.onerror = () => console.warn('[rekrut] не удалось загрузить картинку команды:', url);
  img.src = url;
  return tex;
}

/** Что кэш держит на зрителя: текстура и холст, в котором она живёт. */
interface Entry {
  tex: Texture;
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  /** Настоящая аватарка уже нарисована — второй раз не качаем. */
  real: boolean;
}

export class AvatarCache {
  private readonly map = new Map<string, Entry>();
  private readonly pending = new Set<string>();
  /**
   * Отложенное уничтожение вытесненных текстур.
   *
   * Уничтожать сразу нельзя: спрайт долгоживущего бойца держит ссылку на
   * свою текстуру и к кэшу больше не обращается. Уничтоженная текстура
   * попадала в пакет отрисовки с пустым source, и Pixi падал с
   * «Cannot read properties of null (reading alphaMode)».
   *
   * Поэтому вытесненное сначала уходит в отстойник и уничтожается только
   * через GRACE_MS, когда на него уже точно никто не смотрит.
   */
  private readonly doomed: { tex: Texture; at: number }[] = [];

  constructor(private readonly limit = 1600) {}

  /**
   * Текстура для шара. Возвращает identicon мгновенно, а когда догрузится
   * настоящая аватарка — дорисовывает её В ТОТ ЖЕ холст.
   *
   * Подменять текстуру в кэше на новую нельзя, и это стоило эфира: спрайты,
   * созданные раньше, держат ссылку на старый объект. Настоящая картинка до
   * них не доезжала (шары так и оставались с identicon), а вытесненный
   * identicon через полминуты уничтожался прямо под ними — и рендер падал в
   * чёрный экран. С демо-данными это не воспроизводилось: там подмены нет,
   * identicon рисуется сразу и навсегда.
   */
  get(userId: string, url: string): Texture {
    const have = this.map.get(userId);
    if (have) {
      this.touchInternal(userId, have);
      if (url && !have.real && !this.pending.has(userId)) this.load(userId, url);
      return have.tex;
    }

    const canvas = drawIdenticon(userId);
    const entry: Entry = {
      canvas,
      ctx: canvas.getContext('2d')!,
      tex: Texture.from(canvas),
      real: false,
    };
    entry.tex.label = `av:${userId}`;
    this.map.set(userId, entry);
    if (url) this.load(userId, url);
    this.evict();
    return entry.tex;
  }

  /** Отметить, что текстура ещё нужна: вызывается на каждом кадре. */
  touch(userId: string): void {
    const e = this.map.get(userId);
    if (e) this.touchInternal(userId, e);
  }

  private touchInternal(userId: string, e: Entry): void {
    // Map хранит порядок вставки: удалить и вставить заново — это перенос
    // в конец за O(1), в отличие от поиска по массиву.
    this.map.delete(userId);
    this.map.set(userId, e);
  }

  private load(userId: string, url: string): void {
    if (this.pending.has(userId)) return;
    this.pending.add(userId);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      this.pending.delete(userId);
      const e = this.map.get(userId);
      // Зрителя могли вытеснить, пока картинка ехала.
      if (!e) return;
      const { ctx } = e;
      ctx.save();
      ctx.clearRect(0, 0, SIZE, SIZE);
      ctx.beginPath();
      ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2, 0, Math.PI * 2);
      ctx.clip();
      ctx.drawImage(img, 0, 0, SIZE, SIZE);
      ctx.restore();
      e.real = true;
      // Тот же объект текстуры, новое содержимое: все уже нарисованные шары
      // получают аватарку в этом же кадре.
      e.tex.source.update();
    };
    img.onerror = () => {
      // Оставляем identicon. 403 и отсутствие CORS от CDN TikTok — норма,
      // ради этого и существует прокси на бэкенде.
      this.pending.delete(userId);
    };
    img.src = url;
  }

  private doom(tex: Texture): void {
    this.doomed.push({ tex, at: performance.now() + GRACE_MS });
  }

  /** Добивание отложенных. Вызывается рендером раз в кадр, стоит копейки. */
  sweep(): void {
    const now = performance.now();
    while (this.doomed.length > 0 && this.doomed[0]!.at <= now) {
      this.doomed.shift()!.tex.destroy(true);
    }
  }

  private evict(): void {
    while (this.map.size > this.limit) {
      // Первый ключ Map — самый давний по обращению.
      const oldest = this.map.keys().next();
      if (oldest.done) break;
      const e = this.map.get(oldest.value)!;
      this.map.delete(oldest.value);
      this.doom(e.tex);
    }
  }

  /** Явное освобождение. */
  release(userId: string): void {
    const e = this.map.get(userId);
    if (!e) return;
    this.map.delete(userId);
    this.doom(e.tex);
  }

  get size(): number {
    return this.map.size;
  }
}
